import { useState, useRef, useCallback, useEffect } from 'react';

interface DragDetectionResult {
  isDragging: boolean;
  wasDragging: React.MutableRefObject<boolean>;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleClick: (e: React.MouseEvent) => boolean;
  resetDragging: () => void;
}

/**
 * Custom hook to detect when a mouse interaction is a drag vs a click
 * @param threshold Minimum distance in pixels to consider as dragging
 * @returns Object with drag state and event handlers
 */
export default function useDragDetection(threshold: number = 5): DragDetectionResult {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{x: number, y: number} | null>(null);
  const wasDragging = useRef(false);
  
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Save initial position for drag detection
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    wasDragging.current = false;
  }, []);
  
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragStartPos.current) return;
    
    const dx = Math.abs(e.clientX - dragStartPos.current.x);
    const dy = Math.abs(e.clientY - dragStartPos.current.y);
    
    // If moved more than threshold, consider it a drag
    if (dx > threshold || dy > threshold) {
      wasDragging.current = true;
      setIsDragging(true);
    }
  }, [threshold]);
  
  const handleMouseUp = useCallback(() => {
    // Keep track of whether we were dragging when the mouse was released
    // The click event will check this value
    dragStartPos.current = null;
    setIsDragging(false);
  }, []);
  
  const resetDragging = useCallback(() => {
    wasDragging.current = false;
    setIsDragging(false);
  }, []);
  
  // Handler for click events that immediately checks if we were dragging
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (wasDragging.current) {
      e.stopPropagation();
      resetDragging();
      return false; // Signal that the click should be ignored
    }
    return true; // Signal that the click should proceed
  }, [resetDragging]);
  
  // Reset dragging state on unmount
  useEffect(() => {
    return () => {
      wasDragging.current = false;
    };
  }, []);
  
  return {
    isDragging,
    wasDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleClick,
    resetDragging
  };
} 